import {
  PergolaDesign,
  TextureCode,
  type PlacedAccessory,
  type PrestigeConfigurationData,
} from '@core/entities/prestige-configuration';
import {
  getPrestigeExternalDepthCm,
  getPrestigeExternalHeightCm,
  getPrestigeExternalWidthCm,
} from '@domain/services/prestigeDimensionDisplay';
import { PRESTIGE_DIMENSIONS } from '@domain/constants/prestigeDimensions';

type FeetAndInches = {
  feet: number;
  inches: number;
};

export type LocalCollaborativeState = {
  collaborative: boolean;
  widthValue: number;
  depthValue: number;
  heightValue: number;
  hasWall: boolean;
  frameTexture: string;
  blindTexture: string;
  ledLight: boolean;
  heaterQuantity: number;
  placedAccessories: PlacedAccessory[];
};

export type HydratedCollaborativeState = LocalCollaborativeState & {
  buildNumber: string;
};

const fromCentimetersToFeetAndInches = (centimeters: number): FeetAndInches => {
  const totalInches = centimeters / 2.54;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);

  if (inches === 12) {
    feet += 1;
    inches = 0;
  }

  return { feet, inches };
};

const fromFeetAndInchesToCentimeters = (feetAndInches: FeetAndInches): number =>
  (feetAndInches.feet * 12 + feetAndInches.inches) * 2.54;

const VALID_TEXTURE_CODES = new Set<string>(Object.values(TextureCode));

const ensureTextureCode = (value: string | undefined): TextureCode =>
  value && VALID_TEXTURE_CODES.has(value) ? (value as TextureCode) : TextureCode.RAL_7015;

const normalizePlacedAccessories = (
  accessories: PlacedAccessory[],
): PlacedAccessory[] =>
  accessories
    .map((accessory) => ({
      ...accessory,
      mergeDoors: accessory.mergeDoors ? [...accessory.mergeDoors] : undefined,
    }))
    .sort((first, second) => first.faceId.localeCompare(second.faceId));

const findNearest = (
  target: number,
  candidates: readonly number[],
  getCandidateValue: (candidate: number) => number,
): number => {
  let nearest = candidates[0];
  let minimumDifference = Math.abs(getCandidateValue(nearest) - target);

  for (let index = 1; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const difference = Math.abs(getCandidateValue(candidate) - target);

    if (difference < minimumDifference) {
      minimumDifference = difference;
      nearest = candidate;
    }
  }

  return nearest;
};

export const mapLocalStateToPrestigeConfigurationData = ({
  localState,
  buildNumber,
  ownerID,
}: {
  localState: LocalCollaborativeState;
  buildNumber: string;
  ownerID: string;
}): PrestigeConfigurationData => {
  const externalWidth = getPrestigeExternalWidthCm(localState.widthValue);
  const externalDepth = getPrestigeExternalDepthCm(localState.depthValue);
  const externalHeight = getPrestigeExternalHeightCm(localState.heightValue);

  return {
    ownerID,
    collaborative: localState.collaborative,
    design: {
      pergola: localState.hasWall
        ? PergolaDesign.ATTACHED
        : PergolaDesign.FREE_STANDING,
      structureColor: ensureTextureCode(localState.frameTexture),
      louverColor: ensureTextureCode(localState.blindTexture),
    },
    dimensions: {
      width: fromCentimetersToFeetAndInches(externalWidth),
      length: fromCentimetersToFeetAndInches(externalDepth),
      height: fromCentimetersToFeetAndInches(externalHeight),
    },
    accessories: {
      ledLight: localState.ledLight,
      heaterQuantity: localState.heaterQuantity,
      placed: normalizePlacedAccessories(localState.placedAccessories),
    },
    buildNumber,
  };
};

export const mapPrestigeConfigurationDataToLocalState = (
  data: PrestigeConfigurationData,
): HydratedCollaborativeState => {
  const widthCentimeters = fromFeetAndInchesToCentimeters(data.dimensions.width);
  const depthCentimeters = fromFeetAndInchesToCentimeters(data.dimensions.length);
  const heightCentimeters = fromFeetAndInchesToCentimeters(data.dimensions.height);

  return {
    buildNumber: data.buildNumber,
    collaborative: data.collaborative,
    hasWall: data.design.pergola === PergolaDesign.ATTACHED,
    frameTexture: data.design.structureColor,
    blindTexture: data.design.louverColor,
    ledLight: data.accessories.ledLight,
    heaterQuantity: data.accessories.heaterQuantity ?? 0,
    placedAccessories: normalizePlacedAccessories(data.accessories.placed),
    widthValue: findNearest(
      widthCentimeters,
      PRESTIGE_DIMENSIONS.width,
      getPrestigeExternalWidthCm,
    ),
    depthValue: findNearest(
      depthCentimeters,
      PRESTIGE_DIMENSIONS.depth,
      getPrestigeExternalDepthCm,
    ),
    heightValue: findNearest(
      heightCentimeters,
      PRESTIGE_DIMENSIONS.height,
      getPrestigeExternalHeightCm,
    ),
  };
};
